/* global FBInstant */

import * as constants from '@/constants';
import * as config from '@/config-APP_TARGET';
import Popup from '@/utils/popup';
import runtime from '@/runtime';
import { getModel } from '@/utils/model';
import axios from 'axios';
import * as auth from '@/utils/auth';
import * as audio from '@/utils/audio';

/**
 * Chiave localStorage per tracciare le sfide concluse gia viste
 */
var SEEN_COMPLETED_CHALLENGES_KEY = 'seen_completed_challenges';

export default class Boot extends Phaser.Scene {
	/**
	 *  Takes care of loading the main game assets, including textures, tile
	 *  maps, sound effects and other binary files, while displaying a busy
	 *  splash screen.
	 *
	 *  @extends Phaser.Scene
	 */
	constructor() {
		super({
			key: 'Boot'
		});
	}

	showMessageBox(text, w, h) {
		if (w === undefined) {
			w = 300;
		}
		if (h === undefined) {
			h = 300;
		}
		//just in case the message box already exists
		//destroy it
		if (this.msgBox) {
			this.msgBox.destroy();
		}
		//make a container to hold all the elements (Phaser 3 uses Container instead of Group)
		var msgBox = this.add.container(0, 0);
		//make the back of the message box
		var back = this.add.rectangle(0, 0, w, h, 0x000000, 0.8);
		//make the close button (using a simple rectangle as button if texture doesn't exist)
		var closeButton = this.add.rectangle(0, 0, 50, 50, 0xff0000);
		//make a text field
		var text1 = this.add.text(0, 0, text, {
			fontSize: '20px',
			color: '#ffffff',
			wordWrap: { width: w * 0.9 }
		});
		//
		//set the close button
		//in the center horizontally
		//and near the bottom of the box vertically
		closeButton.x = 0;
		closeButton.y = h / 2 - closeButton.height / 2 - 20;
		//enable the button for input (Phaser 3 uses setInteractive)
		closeButton.setInteractive({ useHandCursor: true });
		//add a listener to destroy the box when the button is pressed
		closeButton.on('pointerdown', this.hideBox, this);
		//
		//
		//set the message box in the center of the screen
		msgBox.x = this.scale.width / 2;
		msgBox.y = this.scale.height / 2;
		//
		//set the text in the middle of the message box
		text1.x = -text1.width / 2;
		text1.y = -text1.height / 2;
		//
		//add the elements to the container
		msgBox.add([back, closeButton, text1]);
		//make a state reference to the messsage box
		this.msgBox = msgBox;
	}

	hideBox() {
		//destroy the box when the button is pressed
		this.msgBox.destroy();
	}

	/**
	 * Boot Scene - Punto unico di controllo autenticazione
	 * Verifica autenticazione e decide se caricare il gioco, mostrare Login o ErrorScene
	 */
	create() {
		var scene = this;

		// Inizializza sistema audio
		audio.initAudio(this);

		var urlParams = new URLSearchParams(window.location.search);
		// Recupera challenge_token dall'URL o dal registry (salvato da Login dopo OTP)
		var challengeToken = urlParams.get('challenge_token') || this.registry.get('pending_challenge_token');

		// Se il token viene dal registry, rimuovilo dopo averlo letto
		if (this.registry.get('pending_challenge_token')) {
			this.registry.remove('pending_challenge_token');
		}

		// Debug: verifica stato localStorage all'avvio
		console.log('Boot create() - Stato localStorage:', {
			'auth_token': localStorage.getItem('auth_token') ? 'presente' : 'assente',
			'tutti_i_key': Object.keys(localStorage)
		});

		// Verifica se l'utente è già autenticato (ha un token JWT valido)
		var savedToken = auth.getToken();
		if (savedToken && auth.isTokenValid(savedToken)) {
			console.log('Utente già autenticato, verifica token lato server...');
			// Imposta header Authorization
			axios.defaults.headers.common['Authorization'] = 'Bearer ' + savedToken;

			// Verifica validità lato server
			auth.verifyToken()
				.then(function(response) {
					var data = response.data;
					if (data.success === true) {
						console.log('Token valido lato server');

						// Salva i dati dell'utente nel localStorage
						if (data.user) {
							auth.saveUser(data.user);
						}

						// Determina tipo autenticazione dal token
						runtime.authType = auth.getAuthType(savedToken);

						// Se c'è challenge_token, accetta direttamente la sfida
						if (challengeToken) {
							console.log('Utente autenticato con challenge_token, accetto sfida automaticamente');
							scene.scene.start('AcceptChallenge', {
								challenge_token: challengeToken,
								autoAccept: true
							});
							auth.cleanUrl();
						} else {
							// Altrimenti carica gioco normalmente
							auth.cleanUrl();
							scene.loadGame();
						}
					} else {
						// Token non valido lato server: rimuovi token e procedi con login
						console.log('Token non valido lato server (success: false), rimuovo token');
						auth.removeToken();
						scene.handleChallengeOrLogin(challengeToken);
					}
				})
				.catch(function(error) {
					console.error('Errore verifica token lato server:', error);

					// Verifica se l'errore è 401 (non autorizzato) o altro
					if (error.response && error.response.status === 401) {
						console.log('Errore 401: token non valido o scaduto, rimuovo token');
						auth.removeToken();
					} else {
						console.log('Errore diverso da 401, mantengo token e riprovo');
						// Per altri errori (rete, server, ecc.), non rimuovere il token
						// L'utente potrebbe essere ancora autenticato
					}

					// Procedi con challenge o login
					scene.handleChallengeOrLogin(challengeToken);
				});
			return;
		}

		// Se non è autenticato, gestisci challenge o mostra login
		this.handleChallengeOrLogin(challengeToken);
	}

	/**
	 * Gestisce challenge_token o mostra login
	 * @param {string|null} challengeToken
	 */
	handleChallengeOrLogin(challengeToken) {
		// Se c'è challenge_token, avvia AcceptChallenge
		if (challengeToken) {
			console.log('Token challenge rilevato:', challengeToken);
			this.scene.start('AcceptChallenge', { challenge_token: challengeToken });
			return;
		}

		// Altrimenti verifica autenticazione normale
		this.checkAuthentication();
	}

	/**
	 * Controlla autenticazione e decide il flusso:
	 * 1. FBInstant disponibile e token valido → carica gioco
	 * 2. Token in localStorage → verifica → se valido carica gioco
	 * 3. Nessun token → mostra Login
	 */
	checkAuthentication() {
		var scene = this;

		// 1. Controlla se FBInstant è disponibile e ha token valido
		var isLocalhost = window.location.hostname === 'localhost' ||
		                    window.location.hostname === '127.0.0.1' ||
		                    window.location.hostname.includes('.lndo.site');

		if (!isLocalhost && typeof FBInstant !== 'undefined' && FBInstant && typeof FBInstant.initializeAsync === 'function') {
			// FBInstant disponibile: prova a inizializzare con timeout
			// Se non risponde entro 3 secondi, procedi con login normale
			var fbInitTimeout = new Promise(function(resolve, reject) {
				setTimeout(function() {
					reject(new Error('FBInstant timeout - non siamo dentro Facebook'));
				}, 3000);
			});

			var fbInitPromise = FBInstant.initializeAsync()
				.then(function() {
					return FBInstant.player.getSignedPlayerInfoAsync();
				});

			Promise.race([fbInitPromise, fbInitTimeout])
				.then(function(result) {
					// FBInstant token valido: carica gioco
					console.log('FBInstant token valido, carico gioco');
					axios.defaults.headers.common['Authorization'] = 'Bearer ' + result.getSignature();
					// Imposta tipo autenticazione Facebook
					runtime.authType = 'facebook';
					scene.loadGame();
				})
				.catch(function(error) {
					console.log('FBInstant non disponibile o errore:', error.message);
					// FBInstant non disponibile: procedi con logica normale
					scene.checkLocalStorageToken();
				});
		} else {
			// FBInstant non disponibile: procedi con logica normale
			this.checkLocalStorageToken();
		}
	}

	/**
	 * Controlla token in localStorage
	 */
	checkLocalStorageToken() {
		var scene = this;
		var savedToken = auth.getToken();

		if (savedToken) {
			console.log('Token trovato in localStorage, verifica validità...');

			// Prima verifica validità lato client (controlla scadenza)
			if (auth.isTokenValid(savedToken)) {
				console.log('Token valido lato client, verifica lato server...');

				// Imposta header Authorization
				axios.defaults.headers.common['Authorization'] = 'Bearer ' + savedToken;

				// Verifica validità lato server
				auth.verifyToken()
					.then(function(response) {
						var data = response.data;
						if (data.success === true) {
							console.log('Token valido lato server, carico gioco');

							// Salva i dati dell'utente nel localStorage
							if (data.user) {
								auth.saveUser(data.user);
							}

							// Determina tipo autenticazione dal token
							runtime.authType = auth.getAuthType(savedToken);

							// Token valido: carica gioco
							scene.loadGame();
						} else {
							console.log('Token non valido lato server');
							// Token non valido: rimuovi token e mostra login
							auth.removeToken();
							scene.scene.start('Login');
						}
					})
					.catch(function(error) {
						console.error('Errore verifica token lato server:', error);
						// Errore verifica: rimuovi token e mostra login
						auth.removeToken();
						scene.scene.start('Login');
					});
			} else {
				console.log('Token scaduto o non valido lato client');
				// Token scaduto o non valido: rimuovi token e mostra login
				auth.removeToken();
				this.scene.start('Login');
			}
		} else {
			console.log('Nessun token in localStorage, mostro Login');
			// Nessun token: mostra Login
			this.scene.start('Login');
		}
	}

	/**
	 * Mostra ErrorScene con messaggio di errore
	 * Quando utente chiude popup, va alla Login Scene
	 * @param {string} message
	 */
	showErrorScene(message) {
		var scene = this;
		this.scene.start('ErrorScene', {
			message: message,
			onRetry: function() {
				// Quando chiude popup errore, va alla Login Scene
				scene.scene.start('Login');
			}
		});
	}

	/**
	 * Ottiene gli ID delle sfide concluse gia viste dal localStorage
	 * @returns {Array<number>}
	 */
	getSeenCompletedChallenges() {
		var stored = localStorage.getItem(SEEN_COMPLETED_CHALLENGES_KEY);
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch (e) {
				return [];
			}
		}
		return [];
	}

	/**
	 * Controlla se ci sono sfide pendenti (da giocare) e sfide concluse (con risultato).
	 * Questa chiamata viene fatta in parallelo senza bloccare il caricamento del gioco.
	 */
	checkPendingChallenges() {
		var scene = this;

		// Chiama entrambe le API in parallelo
		Promise.all([
			axios.get(config.apiUrl + '/api/invites'),
			axios.get(config.apiUrl + '/api/invites/sent')
		])
		.then(function(responses) {
			var receivedChallenges = responses[0].data || [];
			var sentChallenges = responses[1].data || [];

			// Sfide da giocare: ricevute con status WAITING (10) e senza challenged_round
			var pendingToPlay = receivedChallenges.filter(function(invite) {
				return invite.status === 10 && !invite.challenged_round;
			});

			// Sfide concluse: inviate con challenged_round completato (status 30 = CLOSED)
			var completedChallenges = sentChallenges.filter(function(invite) {
				return invite.challenged_round && invite.challenged_round.status === 30;
			});

			// Filtra le sfide concluse per trovare solo quelle NON ancora viste
			var seenIds = scene.getSeenCompletedChallenges();
			var newCompletedChallenges = completedChallenges.filter(function(invite) {
				return seenIds.indexOf(invite.id) === -1;
			});

			console.log('Sfide da giocare:', pendingToPlay.length);
			console.log('Sfide concluse (nuove):', newCompletedChallenges.length);

			// Salva nel registry
			scene.registry.set('pending_challenges_count', pendingToPlay.length);
			scene.registry.set('pending_challenges_data', pendingToPlay);
			scene.registry.set('completed_challenges_count', newCompletedChallenges.length);
			scene.registry.set('completed_challenges_data', newCompletedChallenges);
		})
		.catch(function(error) {
			console.error('Errore controllo sfide:', error);
			// In caso di errore, non bloccare il gioco
			scene.registry.set('pending_challenges_count', 0);
			scene.registry.set('pending_challenges_data', []);
			scene.registry.set('completed_challenges_count', 0);
			scene.registry.set('completed_challenges_data', []);
		});
	}

	/**
	 * Carica il gioco chiamando l'API per iniziare il round
	 * Questo è l'unico punto dove viene caricato il gioco (Scene Game)
	 */
	loadGame() {
		var scene = this;

		// Controlla sfide pendenti in parallelo (non bloccante)
		this.checkPendingChallenges();

		// Reset info sfida corrente (verrà impostata dalla risposta API se presente)
		runtime.currentChallenge = null;

		// Verifica se c'è un game_mode_id nel localStorage
		var game_mode_id = auth.getGameModeId();

		// Se non c'è game_mode_id salvato, vai alla scena di selezione gioco
		if (!game_mode_id) {
			console.log('Nessun game_mode_id nel localStorage, mostro GameSelection');
			this.scene.start('GameSelection');
			return;
		}

		console.log('Usando game_mode_id dal localStorage:', game_mode_id);

		// Verifica se c'è un invite_id (per accettare una sfida)
		var inviteId = this.scene.settings.data ? this.scene.settings.data.invite_id : null;
		var inviteIdParam = inviteId ? '&invite_id=' + inviteId : '';

		axios.get(config.apiUrl + '/api/rounds/start?game_mode_id=' + game_mode_id + inviteIdParam + '&dict_id=420')
			.then(function (response) {
				// handle success
				runtime.user = response.data.user;
				runtime.game = response.data.game;
				// get available time
				runtime.mode = getModel(runtime.game.modes, 'id', game_mode_id);
				runtime.dict = response.data.dict;
				runtime.skills = response.data.skills;
				runtime.superpowers = response.data.superpowers;
				runtime.gamifications = response.data.gamifications;
				runtime.statuses = response.data.statuses;
				runtime.constants = response.data.constants;
				runtime.round = response.data.round;
				runtime.ranking = response.data.ranking;

				// Se la risposta contiene i dati della sfida, impostali in runtime
				if (response.data.invite) {
					runtime.currentChallenge = response.data.invite;
					console.log('Sfida caricata:', runtime.currentChallenge);
				}

				scene.events.emit('gameReady');

			})
			.catch(function (error) {
				// handle error
				console.log("Errore di rete", error);

				// Estrai il messaggio di errore dalla risposta
				var errorMessage = 'Errore di connessione! Riprova più tardi';

				if (error.response && error.response.data) {
					if (error.response.data.message) {
						errorMessage = error.response.data.message;
					} else if (error.response.data.success === false) {
						errorMessage = error.response.data.message || errorMessage;
					}
				}

				// Mostra ErrorScene
				scene.showErrorScene(errorMessage);
			});

		/****************************************************************
		 *
		 * EVENTO gameReady
		 *
		 ***************************************************************/
		this.events.once(
			'gameReady',
			function () {
				// Avvia Scene Game (unico punto di caricamento gioco)
				this.scene.start('Game');
			},
			this
		);
	}

}
